import React, { useState, useRef, useLayoutEffect, useEffect, useId, KeyboardEvent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ChevronDown, X } from 'lucide-react';
import { DropdownPanel, DropdownOption } from './DropdownPanel';

export interface MultiSelectFieldProps {
  id?: string;
  name: string;
  value: (string | number)[];
  options: DropdownOption[];
  onChange: (value: (string | number)[]) => void;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
  label?: string;
  labelKey?: string;
  placeholder?: string;
  placeholderKey?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  containerClassName?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  errorText?: string;
  errorTextKey?: string;
  inputRef?: React.Ref<HTMLDivElement>;
  portalContainer?: HTMLElement | null;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  id,
  name,
  value = [],
  options,
  onChange,
  onBlur,
  onFocus,
  label,
  labelKey,
  placeholder,
  placeholderKey,
  fullWidth = false,
  disabled = false,
  required = false,
  className,
  labelClassName,
  containerClassName,
  triggerClassName,
  dropdownClassName,
  optionClassName,
  errorText,
  errorTextKey,
  inputRef,
  portalContainer,
}) => {
  const generatedId = useId();
  const uniqueId = id || generatedId;
  const triggerId = `${uniqueId}-trigger`;
  const listboxId = `${uniqueId}-listbox`;
  const labelId = `${uniqueId}-label`;
  const errorId = `${uniqueId}-error`;

  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const internalTriggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const setRefs = (node: HTMLDivElement | null) => {
    (internalTriggerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (typeof inputRef === 'function') {
      inputRef(node);
    } else if (inputRef) {
      (inputRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedPlaceholder = placeholderKey ? t(placeholderKey) : placeholder;
  const resolvedError = errorTextKey ? t(errorTextKey) : errorText;
  const hasError = !!resolvedError;

  const selectedOptions = useMemo(() => options.filter(opt => value.includes(opt.value)), [options, value]);

  const setInitialPosition = useCallback(() => {
    const el = internalTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownStyles({
      position: 'fixed',
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      zIndex: 1001,
    });
  }, []);

  const clampAndFlipPosition = useCallback(() => {
    const trigger = internalTriggerRef.current;
    const panel = dropdownRef.current;
    if (!isOpen || !trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();

    let top = rect.bottom + 6;
    let left = rect.left;

    const availableBelow = window.innerHeight - rect.bottom - 6;
    const availableAbove = rect.top - 6;

    if (panelRect.height > availableBelow && availableAbove > panelRect.height) {
      top = Math.max(8, rect.top - panelRect.height - 6);
    }

    if (left + panelRect.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelRect.width - 8);
    }
    if (left < 8) left = 8;

    setDropdownStyles({
      position: 'fixed',
      top: Math.round(top),
      left: Math.round(left),
      width: Math.round(rect.width),
      zIndex: 1001,
    });
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    clampAndFlipPosition();

    const handle = () => clampAndFlipPosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [clampAndFlipPosition, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && internalTriggerRef.current && !internalTriggerRef.current.contains(event.target as Node) && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionClick = (optionValue: string | number) => {
    if (disabled) return;
    const newValues = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValues);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsOpen(prev => {
      const opening = !prev;
      if (opening) setInitialPosition();
      return opening;
    });
  };

  const handleBadgeRemove = (e: React.MouseEvent, optionValue: string | number) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter(v => v !== optionValue));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === 'Escape') {
      setIsOpen(false);
      internalTriggerRef.current?.focus();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isOpen) setInitialPosition();
      setIsOpen(prev => !prev);
      return;
    }
    if (event.key === 'ArrowDown' && !isOpen) {
      event.preventDefault();
      setInitialPosition();
      setIsOpen(true);
    }
  };

  const dropdownJsx = (
    <DropdownPanel
      ref={dropdownRef}
      isOpen={isOpen}
      options={options}
      value={value}
      activeIndex={activeIndex}
      onOptionClick={handleOptionClick}
      onOptionMouseEnter={setActiveIndex}
      listboxId={listboxId}
      labelId={resolvedLabel ? labelId : undefined}
      uniqueIdPrefix={uniqueId}
      className={dropdownClassName}
      optionClassName={optionClassName}
      popperStyles={dropdownStyles}
      popperAttributes={undefined}
      isMultiSelect={true}
      portalContainer={portalContainer}
    />
  );

  const hasContent = value && value.length > 0;

  return (
    <div className={clsx('flex flex-col', fullWidth ? 'w-full' : 'w-auto', className)}>
      {resolvedLabel && (
        <label
          onClick={() => internalTriggerRef.current?.focus()}
          id={labelId}
          className={clsx(
            'block text-sm font-semibold text-slate-700 mb-1.5 cursor-pointer',
            '[.theme-liquid-glass_&]:text-white/90',
            disabled && 'opacity-70',
            hasError && 'text-red-600 [.theme-liquid-glass_&]:text-rose-400',
            labelClassName
          )}
        >
          {resolvedLabel}
          {required && <span className="ml-1 text-slate-500 [.theme-liquid-glass_&]:text-white/50">*</span>}
        </label>
      )}

      <div className={clsx('relative group', containerClassName)}>
        {!disabled && (
          <div
            className={clsx(
              'absolute inset-0 rounded-lg bg-black transition-transform duration-150 ease-out',
              '[.theme-liquid-glass_&]:hidden',
              isOpen ? 'translate-x-0.5 translate-y-0.5' : 'group-hover:translate-x-0.5 group-hover:translate-y-0.5'
            )}
            aria-hidden="true"
          />
        )}
        <div
          className={clsx(
            'relative flex items-center bg-white rounded-lg border-2 min-h-[46px]',
            disabled ? 'border-slate-300 bg-slate-100' : hasError ? 'border-red-500' : 'border-black',
            '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
            '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:text-white',
            '[.theme-liquid-glass_&]:rounded-xl [.theme-liquid-glass_&]:backdrop-blur-xl',
            !disabled && 'transition-transform duration-150 ease-out',
            !disabled && (isOpen ? '-translate-x-0.5 -translate-y-0.5' : 'group-hover:-translate-x-0.5 group-hover:-translate-y-0.5'),
            !disabled && isOpen && '[.theme-liquid-glass_&]:translate-x-0 [.theme-liquid-glass_&]:translate-y-0',
            !disabled && !isOpen && '[.theme-liquid-glass_&]:group-hover:translate-x-0 [.theme-liquid-glass_&]:group-hover:translate-y-0'
          )}
        >

          <div
            ref={setRefs}
            id={triggerId}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={handleTriggerClick}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-labelledby={resolvedLabel ? `${labelId} ${triggerId}` : triggerId}
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-required={required}
            className={clsx(
              'flex-grow flex flex-wrap items-center gap-2 w-full appearance-none bg-transparent rounded-lg py-2 pr-10 pl-3.5',
              '[.theme-liquid-glass_&]:rounded-xl',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              'focus:outline-none',
              triggerClassName
            )}
          >
            {hasContent ? (
              selectedOptions.map(option => (
                <span
                  key={option.value}
                  className={clsx(
                    'flex items-center gap-1.5 bg-slate-200 text-slate-800 text-sm font-medium pl-2 pr-1 py-0.5 rounded',
                    '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white/85',
                    '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20'
                  )}
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={(e) => handleBadgeRemove(e, option.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                    className={clsx(
                      'hover:bg-slate-300 rounded-full p-0.5',
                      '[.theme-liquid-glass_&]:hover:bg-white/20'
                    )}
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-slate-400 [.theme-liquid-glass_&]:text-white/50">{resolvedPlaceholder || 'Select...'}</span>
            )}
          </div>

          <div className={clsx('absolute right-0 top-0 bottom-0 flex items-center pr-3 pointer-events-none', disabled && 'opacity-50')}>
            <ChevronDown
              className={clsx(
                'h-5 w-5 transition-transform duration-200',
                isOpen && 'transform rotate-180',
                hasError ? 'text-red-500 [.theme-liquid-glass_&]:text-rose-400' : 'text-slate-400 [.theme-liquid-glass_&]:text-white/50'
              )}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <select
        multiple
        tabIndex={-1}
        aria-hidden="true"
        name={`${name}[]`}
        value={value.map(selectedValue => String(selectedValue))}
        onChange={() => {}}
        required={required}
        disabled={disabled}
        className="absolute h-px w-px opacity-0 pointer-events-none"
      >
        {options.map(option => (
          <option key={option.value} value={String(option.value)} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>

      {isOpen && dropdownJsx}

      {hasError && (
        <p id={errorId} className="mt-1.5 text-xs text-red-600 font-medium [.theme-liquid-glass_&]:text-rose-400">
          {resolvedError}
        </p>
      )}
    </div>
  );
};
